import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import React, { useContext, useMemo } from "react";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context.tsx";
import { checkSearch } from "@shared/utils/misc.ts";
import Select from "common/Select/Select.tsx";
import { ICON_TYPES } from "@shared/common/constants.ts";
import TextWithIcon from "common/Text/TextWithIcon.tsx";
import { difference, findKey, intersection } from "lodash";
import styled from "styled-components";
import { PairButton } from "features/bots/BotForm/General/PairButtons.tsx";
import { openMessage } from "utils/misc.tsx";
import {
  ExchangeTag,
  OnChangeWithWarning,
} from "@shared/features/tradingTerminal/common/types.ts";
import { sortByStringProperty } from "@shared/utils/general.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import { isSymbol } from "@shared/types/utils.ts";
import { BotFormType } from "@shared/features/bots/BotForm/common/types.ts";

interface ExchangesSelectorProps {
  onChangeWithWarning?: OnChangeWithWarning;
  allExchanges: string[];
  tagToValidExchanges: Record<ExchangeTag, string[]>;
  exchangeToError: Record<string, string>;
}

const ExchangesSelector = ({
  onChangeWithWarning,
  tagToValidExchanges,
  allExchanges,
  exchangeToError,
}: ExchangesSelectorProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const tradingTerminalData = useContext(TradingTerminalContext);
  const botFormData = useContext(BotFormContext);
  const { exchanges: rawExchanges, setExchanges } =
    tradingTerminalData ?? botFormData ?? {};
  const { botFormType } = botFormData ?? {};
  const exchanges = isSymbol(rawExchanges) ? undefined : rawExchanges;
  const { setAccountChanged, useFreeTextData } = tradingTerminalData || {};

  const options = useMemo(() => {
    return allExchanges.map((exchange) => {
      const disabled = !!exchangeToError[exchange];
      return {
        value: exchange,
        label: (
          <TextWithIcon
            coinOrExchangeName={exchange}
            text={exchange}
            iconType={ICON_TYPES.EXCHANGE}
            style={{ opacity: disabled ? 0.4 : 1 }}
          />
        ),
      };
    });
  }, [allExchanges, exchangeToError]);

  const onExchangesChange = (exchanges: string[]) => {
    setExchanges?.([...exchanges].sort(sortByStringProperty));
    if (useFreeTextData) {
      setAccountChanged?.(true);
    }
  };

  const onChange = (updatedExchanges: string[]) => {
    const addedExchanges = difference(updatedExchanges, exchanges || []);
    const errorMessage = exchangeToError[addedExchanges[0]];
    if (errorMessage) {
      openMessage(errorMessage, "error");
    } else {
      if (onChangeWithWarning) {
        onChangeWithWarning(() =>
          onExchangesChange(intersection(allExchanges, updatedExchanges))
        ).catch(() => {});
      } else {
        onExchangesChange(intersection(allExchanges, updatedExchanges));
      }
    }
  };

  const selectedTag = useMemo(() => {
    return findKey(tagToValidExchanges, (validExchanges) => {
      return (
        !!validExchanges.length &&
        validExchanges.every((validExchange) =>
          exchanges?.includes(validExchange)
        )
      );
    });
  }, [tagToValidExchanges, exchanges]);

  const onTagClick = (tag: ExchangeTag) => {
    const tagExchanges = tagToValidExchanges[tag];
    if (tagExchanges.length) {
      onExchangesChange(tagExchanges);
    } else {
      openMessage(
        t("messages.errors.not_valid_symbol_in_exchange", {
          tag: t(tag).toLowerCase(),
        }),
        "warning"
      );
    }
  };

  const getExchangeTags = () => {
    if (botFormType === BotFormType.BULK_EDIT) return null;
    return (
      <TagsWrapper>
        {Object.values(ExchangeTag).map((exchangeTag) => {
          const isInvalid = !tagToValidExchanges[exchangeTag].length;
          return (
            <Tag
              key={exchangeTag}
              $selected={exchangeTag === selectedTag}
              $isInvalid={isInvalid}
              onClick={() => onTagClick(exchangeTag)}
            >
              {t(exchangeTag)}
            </Tag>
          );
        })}
      </TagsWrapper>
    );
  };

  return (
    <>
      <Select
        style={{ width: "100%" }}
        value={exchanges}
        placeholder={
          isSymbol(rawExchanges)
            ? t("placeholders.multi")
            : t("placeholders.choose_exchanges")
        }
        onChange={onChange}
        popupMatchSelectWidth={false}
        optionLabelProp="label"
        showSearch={{
          filterOption: (searchValue, option) => {
            return checkSearch(option?.value as string, searchValue);
          },
        }}
        mode="tags"
        options={options}
        mobileTitle={t("labels.exchanges")}
      />
      {getExchangeTags()}
    </>
  );
};

export default ExchangesSelector;

const TagsWrapper = styled.div`
  margin-top: 6px;
  display: flex;
  column-gap: 6px;
  ${(p) => p.theme.isNewLook && `margin-top: 8px;`}
`;
const Tag = styled(PairButton)<{ $selected?: boolean; $isInvalid?: boolean }>`
  border-radius: 6px;
  ${(p) =>
    p.$isInvalid &&
    `
    opacity: 0.4;
  `};
  ${(p) =>
    p.theme.isNewLook &&
    `
    /* Quick-select chips: quiet outlined pills, tinted when selected. */
    height: 26px;
    padding: 0 10px;
    border-radius: ${NEW_LOOK_RADIUS.chip}px;
    font-size: 12.5px;
    background-color: ${p.$selected ? p.theme.colors.lightBlue : "transparent"};
    color: ${
      p.$selected ? p.theme.colors.primaryBlue : p.theme.colors.secondaryText
    };
    box-shadow: inset 0 0 0 1px ${
      p.$selected ? "transparent" : p.theme.colors.lightBorderGrey
    };
    transition: color 150ms, background-color 150ms, box-shadow 150ms;
    &:hover {
      color: ${
        p.$selected ? p.theme.colors.primaryBlue : p.theme.colors.primaryText
      };
    }
  `};
`;
